/**
 * Payment Mapper
 *
 * Transforms backend DTOs (LC_*) to UI models
 */

import { formatToDdMmYyyyHHmmss } from '@/utils';
import { PAYMENT_CONCEPT_ERROR_STATUSES } from './payment.constants';
import type {
  LC_PaymentConceptItemOut,
  LC_GetPaymentConceptsListOut,
  PaymentConceptItem,
  PaymentConceptsListResult,
} from './payment.types';

/**
 * Replace null or empty string with '-'
 */
const replaceEmpty = (value: string | null | undefined): string => {
  if (!value || value.trim() === '') return '-';
  return value;
};

/**
 * Format a decimal value for display, replacing null with '-'
 */
const formatValue = (value: number | null): string => {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Format a date string, replacing null with '-'
 */
const formatDate = (isoDate: string | null): string => {
  if (!isoDate) return '-';
  return formatToDdMmYyyyHHmmss(isoDate);
};

/**
 * Map single payment concept item from backend to UI model
 */
export const mapPaymentConceptItem = (item: LC_PaymentConceptItemOut): PaymentConceptItem => ({
  employeeTaxIdentificationNumber: replaceEmpty(item.employeeTaxIdentificationNumber),
  employeeName: replaceEmpty(item.employeeName),
  company: replaceEmpty(item.company),
  contract: String(item.contract),
  concept: replaceEmpty(item.concept), // = idRex del concepto
  conceptName: replaceEmpty(item.conceptName),
  value: formatValue(item.value),
  generatedAt: formatDate(item.generatedAt),
  previousValue: formatValue(item.previousValue),
  sentDate: formatDate(item.sentDate),
  // El backend siempre normaliza status a un texto no vacío (ver PaymentConceptStatusConstants).
  status: item.status ?? '-',
  // Ya filtrado y deduplicado por el backend (NE_PaymentConceptObservationHelper); '?? []' es
  // una red de seguridad ante una respuesta malformada.
  errorMessages: item.errorMessages ?? [],
  hasError: PAYMENT_CONCEPT_ERROR_STATUSES.includes(item.status ?? ''),
});

/**
 * Map full payment concepts list response to UI result
 */
export const mapPaymentConceptsListResponse = (
  response: LC_GetPaymentConceptsListOut,
): PaymentConceptsListResult => ({
  data: (response.data ?? []).map(mapPaymentConceptItem),
  page: response.pagination.currentPage,
  pageSize: response.pagination.pageSize,
  totalRecords: response.pagination.totalRecords,
  totalPages: response.pagination.totalPages,
});
